<template>
  <div class="ivr-audio-layout">
    <Sidebar />
    <div class="main-content">
      <TopHeader pageTitle="IVR Audio Library" />
      <main class="page-content">

        <!-- Page Header -->
        <div class="page-header">
          <div class="header-content">
            <h1>Audio Library</h1>
            <p class="subtitle">Manage TTS-generated and uploaded audio files for your IVR flows.</p>
          </div>
          <div class="header-actions">
            <button class="btn-secondary" @click="showUploadModal = true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z"/></svg>
              Upload File
            </button>
            <button class="create-btn" @click="showTTSModal = true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>
              Generate TTS
            </button>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
        </div>
        <div v-else-if="error" class="error-state">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
          {{ error }}
        </div>

        <!-- Audio Grid -->
        <div v-else class="audio-grid">
          <div v-for="file in files" :key="file.id" class="audio-card">
            <div class="audio-card-header">
              <div class="card-badges">
                <span class="source-badge" :class="'source-' + file.source">{{ file.source }}</span>
                <span v-if="file.tts_provider" class="provider-badge" :class="'provider-' + file.tts_provider">{{ file.tts_provider }}</span>
              </div>
              <span class="duration-text" v-if="file.duration_sec">{{ file.duration_sec }}s</span>
            </div>
            <div class="audio-name">{{ file.name }}</div>
            <div class="audio-meta">
              <span v-if="file.tts_voice">{{ file.tts_voice }}</span>
              <span v-if="file.file_size">{{ formatSize(file.file_size) }}</span>
            </div>
            <div class="audio-card-footer">
              <!-- Loading -->
              <div v-if="previewLoadingId === file.id" class="mini-loading">
                <span class="mini-spinner"></span>
                Loading…
              </div>
              <!-- Player card -->
              <div v-else-if="playingId === file.id" class="mini-player">
                <button class="mini-playpause" @click.stop="previewPaused ? resumeAudio() : pauseAudio()">
                  <svg v-if="previewPaused" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style="display:block"><path d="M8 5v14l11-7z"/></svg>
                  <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style="display:block"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                </button>
                <div class="mini-progress-wrap" @click="seekAudio">
                  <div class="mini-progress-fill" :style="{ width: previewDuration ? (previewCurrentTime / previewDuration * 100) + '%' : '0%' }"></div>
                </div>
                <span class="mini-time">{{ formatTime(previewCurrentTime) }} / {{ formatTime(previewDuration) }}</span>
                <button class="mini-stop" @click.stop="stopAudio" title="Stop">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" style="display:block"><path d="M6 6h12v12H6z"/></svg>
                </button>
              </div>
              <!-- Default -->
              <button v-else class="preview-btn" @click="playAudio(file)">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" style="display:block"><path d="M8 5v14l11-7z"/></svg>
                Preview
              </button>
              <button class="delete-icon-btn" title="Delete" @click="confirmDelete(file)">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
              </button>
            </div>
          </div>

          <div v-if="!files.length" class="empty-state">
            <div class="empty-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/></svg>
            </div>
            <h3>No audio files yet</h3>
            <p>Generate TTS or upload an audio file.</p>
          </div>
        </div>

        <!-- Hidden audio element for preview -->
        <audio ref="audioEl"
          @ended="playingId = null; previewPaused = false; previewCurrentTime = 0"
          @loadedmetadata="previewDuration = $refs.audioEl.duration"
          @timeupdate="previewCurrentTime = $refs.audioEl.currentTime"
          style="display:none">
        </audio>

        <!-- TTS Modal -->
        <div v-if="showTTSModal" class="modal-overlay" @click.self="showTTSModal = false">
          <div class="modal-container">
            <div class="modal-header">
              <h2>Generate TTS Audio</h2>
              <button class="modal-close" @click="showTTSModal = false">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              </button>
            </div>
            <div class="modal-body">
              <div class="form-group">
                <label class="form-label">Name *</label>
                <input v-model="ttsForm.name" class="form-input" placeholder="e.g. Welcome Message" />
              </div>
              <div class="form-group">
                <label class="form-label">Text *</label>
                <textarea v-model="ttsForm.text" class="form-input form-textarea" rows="4" placeholder="Welcome to our service. Press 1 for Sales..."></textarea>
              </div>
              <div class="form-group">
                <label class="form-label">Provider</label>
                <div class="provider-toggle">
                  <button
                    class="provider-btn"
                    :class="{ active: ttsForm.provider === 'openai' }"
                    @click="setProvider('openai')"
                    type="button"
                  >OpenAI</button>
                  <button
                    class="provider-btn"
                    :class="{ active: ttsForm.provider === 'elevenlabs' }"
                    @click="setProvider('elevenlabs')"
                    type="button"
                  >ElevenLabs</button>
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">Voice</label>
                <div v-if="ttsForm.provider === 'openai'" class="voice-chips">
                  <button
                    v-for="v in openAiVoices"
                    :key="v"
                    class="voice-chip"
                    :class="{ active: ttsForm.voice === v }"
                    @click="ttsForm.voice = v"
                    type="button"
                  >{{ v }}</button>
                </div>
                <div v-else>
                  <input v-model="ttsForm.voice" class="form-input" placeholder="e.g. Rachel, Adam, Josh" />
                  <div class="voice-chips" style="margin-top:8px;">
                    <button
                      v-for="v in elevenLabsVoices"
                      :key="v"
                      class="voice-chip"
                      :class="{ active: ttsForm.voice === v }"
                      @click="ttsForm.voice = v"
                      type="button"
                    >{{ v }}</button>
                  </div>
                </div>
              </div>
              <div v-if="ttsPreviewUrl" class="tts-preview">
                <p class="preview-label">Preview:</p>
                <audio :src="ttsPreviewUrl" controls style="width:100%; margin-top:8px;"></audio>
              </div>
            </div>
            <div class="modal-footer">
              <button class="btn-secondary" @click="showTTSModal = false">Cancel</button>
              <button class="btn-secondary" :disabled="generatingTTS || !ttsForm.text" @click="previewTTS">
                {{ generatingTTS ? 'Generating...' : 'Preview' }}
              </button>
              <button class="btn-primary" :disabled="generatingTTS || !ttsForm.name || !ttsForm.text" @click="generateTTS">
                {{ generatingTTS ? 'Saving...' : 'Save to Library' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Upload Modal -->
        <div v-if="showUploadModal" class="modal-overlay" @click.self="showUploadModal = false">
          <div class="modal-container">
            <div class="modal-header">
              <h2>Upload Audio File</h2>
              <button class="modal-close" @click="showUploadModal = false">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              </button>
            </div>
            <div class="modal-body">
              <div class="form-group">
                <label class="form-label">Name (optional)</label>
                <input v-model="uploadForm.name" class="form-input" placeholder="Defaults to filename" />
              </div>
              <div class="form-group">
                <input
                  ref="fileInput"
                  type="file"
                  accept=".mp3,.wav,audio/mpeg,audio/wav"
                  style="display:none"
                  @change="onFileSelect"
                />
                <div
                  class="drop-zone"
                  :class="{ 'drag-over': uploadDragging, 'has-file': !!uploadForm.file }"
                  @dragenter.prevent="uploadDragging = true"
                  @dragover.prevent="uploadDragging = true"
                  @dragleave.prevent="uploadDragging = false"
                  @drop.prevent="onFileDrop"
                  @click="$refs.fileInput.click()"
                >
                  <template v-if="!uploadForm.file">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" class="drop-icon"><path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z"/></svg>
                    <span class="drop-primary">Drop your MP3 or WAV here</span>
                    <span class="drop-secondary">or click to browse files</span>
                  </template>
                  <template v-else>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" class="drop-file-icon"><path d="M12 3v9.28c-.47-.17-.97-.28-1.5-.28C8.01 12 6 14.01 6 16.5S8.01 21 10.5 21c2.31 0 4.2-1.75 4.45-4H15V6h4V3h-7z"/></svg>
                    <div class="drop-file-info">
                      <span class="drop-file-name">{{ uploadForm.file.name }}</span>
                      <span class="drop-file-size">{{ formatSize(uploadForm.file.size) }} · click to change</span>
                    </div>
                  </template>
                </div>
                <p v-if="uploadError" class="upload-error">{{ uploadError }}</p>
              </div>
            </div>
            <div class="modal-footer">
              <button class="btn-secondary" @click="showUploadModal = false">Cancel</button>
              <button class="btn-primary" :disabled="uploading || !uploadForm.file" @click="uploadFile">
                {{ uploading ? 'Uploading...' : 'Upload' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Delete Confirm -->
        <div v-if="deleteTarget" class="modal-overlay" @click.self="deleteTarget = null">
          <div class="modal-container modal-sm">
            <div class="modal-header">
              <h2>Delete Audio</h2>
              <button class="modal-close" @click="deleteTarget = null">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              </button>
            </div>
            <div class="modal-body">
              <p class="confirm-text">Delete <strong>{{ deleteTarget.name }}</strong>? This cannot be undone.</p>
            </div>
            <div class="modal-footer">
              <button class="btn-secondary" @click="deleteTarget = null">Cancel</button>
              <button class="btn-danger" :disabled="deleting" @click="doDelete">{{ deleting ? 'Deleting...' : 'Delete' }}</button>
            </div>
          </div>
        </div>

      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { IVRService } from '@/services/ivrService'

export default {
  name: 'IVRAudioView',
  components: { Sidebar, TopHeader },
  data () {
    return {
      files: [],
      loading: true,
      error: null,
      playingId: null,
      previewPaused: false,
      previewLoadingId: null,
      previewDuration: 0,
      previewCurrentTime: 0,
      showTTSModal: false,
      showUploadModal: false,
      ttsForm: { name: '', text: '', voice: 'alloy', provider: 'openai' },
      ttsPreviewUrl: null,
      generatingTTS: false,
      uploadForm: { name: '', file: null },
      uploading: false,
      uploadDragging: false,
      uploadError: '',
      deleteTarget: null,
      deleting: false,
      openAiVoices: ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'],
      elevenLabsVoices: ['Rachel', 'Adam', 'Josh', 'Antoni', 'Bella']
    }
  },
  async mounted () { await this.load() },
  methods: {
    async load () {
      this.loading = true
      this.error = null
      try {
        const res = await IVRService.getAudio()
        this.files = res.files || []
      } catch (e) {
        this.error = e.message
      } finally {
        this.loading = false
      }
    },

    async playAudio (file) {
      const el = this.$refs.audioEl
      // Resume if same file is paused
      if (this.playingId === file.id && this.previewPaused) {
        await el.play()
        this.previewPaused = false
        return
      }
      // Already playing same file — ignore (use pause/stop buttons)
      if (this.playingId === file.id) return
      if (el.src && el.src.startsWith('blob:')) URL.revokeObjectURL(el.src)
      el.pause()
      this.playingId = null
      this.previewPaused = false
      this.previewLoadingId = file.id
      this.previewDuration = 0
      this.previewCurrentTime = 0
      try {
        const blobUrl = await IVRService.getAudioBlobUrl(file.id)
        el.src = blobUrl
        el.load()
        await el.play()
        this.playingId = file.id
      } catch (e) {
        if (e.name !== 'AbortError') alert('Preview error: ' + e.message)
      } finally {
        this.previewLoadingId = null
      }
    },

    pauseAudio () {
      this.$refs.audioEl.pause()
      this.previewPaused = true
    },

    resumeAudio () {
      this.$refs.audioEl.play()
      this.previewPaused = false
    },

    stopAudio () {
      const el = this.$refs.audioEl
      el.pause()
      el.currentTime = 0
      this.playingId = null
      this.previewPaused = false
      this.previewCurrentTime = 0
    },

    seekAudio (e) {
      const el = this.$refs.audioEl
      if (!this.previewDuration) return
      const rect = e.currentTarget.getBoundingClientRect()
      const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
      el.currentTime = pct * this.previewDuration
    },

    formatTime (secs) {
      if (!secs || isNaN(secs)) return '0:00'
      const m = Math.floor(secs / 60)
      const s = Math.floor(secs % 60)
      return `${m}:${s.toString().padStart(2, '0')}`
    },

    setProvider (p) {
      this.ttsForm.provider = p
      this.ttsForm.voice = p === 'openai' ? 'alloy' : 'Rachel'
    },

    async generateTTS () {
      this.generatingTTS = true
      try {
        await IVRService.generateTTS({
          name: this.ttsForm.name,
          text: this.ttsForm.text,
          voice: this.ttsForm.voice,
          provider: this.ttsForm.provider
        })
        this.showTTSModal = false
        this.ttsForm = { name: '', text: '', voice: 'alloy', provider: 'openai' }
        this.ttsPreviewUrl = null
        await this.load()
      } catch (e) {
        alert('Error: ' + e.message)
      } finally {
        this.generatingTTS = false
      }
    },

    async previewTTS () {
      if (!this.ttsForm.text) return
      this.generatingTTS = true
      try {
        const res = await IVRService.generateTTS({
          name: this.ttsForm.name || 'Preview',
          text: this.ttsForm.text,
          voice: this.ttsForm.voice,
          provider: this.ttsForm.provider
        })
        const urlRes = await IVRService.getAudioUrl(res.id)
        this.ttsPreviewUrl = urlRes.url
      } catch (e) {
        alert('Preview error: ' + e.message)
      } finally {
        this.generatingTTS = false
      }
    },

    onFileSelect (e) {
      this.uploadForm.file = e.target.files[0] || null
      this.uploadError = ''
      if (this.uploadForm.file && !this.uploadForm.name) {
        this.uploadForm.name = this.uploadForm.file.name.replace(/\.[^.]+$/, '')
      }
    },

    onFileDrop (e) {
      this.uploadDragging = false
      const file = e.dataTransfer.files[0]
      if (!file) return
      if (!/\.(mp3|wav)$/i.test(file.name) && !/^audio\/(mpeg|wav)$/.test(file.type)) {
        this.uploadError = 'Only MP3 and WAV files are supported'
        return
      }
      this.uploadError = ''
      this.uploadForm.file = file
      if (!this.uploadForm.name) this.uploadForm.name = file.name.replace(/\.[^.]+$/, '')
    },

    async uploadFile () {
      if (!this.uploadForm.file) return
      this.uploading = true
      try {
        const fd = new FormData()
        fd.append('audio', this.uploadForm.file)
        if (this.uploadForm.name) fd.append('name', this.uploadForm.name)
        await IVRService.uploadAudio(fd)
        this.showUploadModal = false
        this.uploadForm = { name: '', file: null }
        this.uploadError = ''
        this.uploadDragging = false
        await this.load()
      } catch (e) {
        alert('Error: ' + e.message)
      } finally {
        this.uploading = false
      }
    },

    confirmDelete (file) { this.deleteTarget = file },

    async doDelete () {
      this.deleting = true
      try {
        await IVRService.deleteAudio(this.deleteTarget.id)
        this.deleteTarget = null
        await this.load()
      } catch (e) {
        alert('Error: ' + e.message)
      } finally {
        this.deleting = false
      }
    },

    formatSize (bytes) {
      if (!bytes) return ''
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / 1024 / 1024).toFixed(1) + ' MB'
    }
  }
}
</script>

<style scoped>
.ivr-audio-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
}
.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}
.page-content {
  flex: 1;
  padding: 24px 32px;
  overflow-y: auto;
}

/* Page Header */
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 28px;
}
.header-content h1 {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 4px;
}
.subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}
.header-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}
.create-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #3B82F6;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  white-space: nowrap;
}
.create-btn:hover {
  background: #2563EB;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59,130,246,0.4);
}

/* Loading / Error / Empty */
.loading-state {
  display: flex;
  justify-content: center;
  padding: 80px 0;
}
.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid var(--border-primary);
  border-top-color: #3B82F6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.error-state {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: 12px;
  padding: 16px 20px;
  color: #DC2626;
  font-size: 14px;
}

.empty-state {
  grid-column: 1/-1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 80px 20px;
  color: var(--text-secondary);
  text-align: center;
}
.empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  opacity: 0.5;
}
.empty-state h3 {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}
.empty-state p { font-size: 14px; margin: 0; }

/* Audio Grid */
.audio-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
}
.audio-card {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.audio-card:hover {
  border-color: var(--border-secondary);
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}
.audio-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.card-badges {
  display: flex;
  gap: 6px;
  align-items: center;
  flex-wrap: wrap;
}
.source-badge, .provider-badge {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 3px 10px;
  border-radius: 6px;
}
.source-tts { background: rgba(139,92,246,0.1); color: #8B5CF6; }
.source-upload { background: rgba(16,185,129,0.1); color: #10B981; }
.provider-openai { background: rgba(59,130,246,0.1); color: #3B82F6; }
.provider-elevenlabs { background: rgba(236,72,153,0.1); color: #EC4899; }

/* Provider toggle */
.provider-toggle {
  display: flex;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  overflow: hidden;
}
.provider-btn {
  flex: 1;
  padding: 9px 16px;
  border: none;
  background: var(--bg-primary);
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s;
}
.provider-btn + .provider-btn { border-left: 1px solid var(--border-primary); }
.provider-btn.active {
  background: #3B82F6;
  color: #fff;
  font-weight: 600;
}

/* Voice chips */
.voice-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.voice-chip {
  padding: 6px 14px;
  border: 1px solid var(--border-primary);
  border-radius: 20px;
  background: var(--bg-primary);
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s;
  text-transform: capitalize;
}
.voice-chip:hover { border-color: #3B82F6; color: #3B82F6; }
.voice-chip.active { background: #3B82F6; color: #fff; border-color: #3B82F6; }
.duration-text {
  font-size: 12px;
  color: var(--text-secondary);
}
.audio-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.3;
}
.audio-meta {
  display: flex;
  gap: 10px;
  font-size: 12px;
  color: var(--text-secondary);
}
.audio-card-footer {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 4px;
}
.preview-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-primary);
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
  transition: all 0.2s;
}
.preview-btn:hover { background: var(--bg-tertiary); border-color: var(--border-secondary); }

/* ── Loading row ── */
.mini-loading {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 4px;
  font-size: 12px;
  color: var(--text-secondary);
  height: 36px;
}
.mini-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid var(--border-primary);
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: mini-spin 0.7s linear infinite;
  flex-shrink: 0;
}
@keyframes mini-spin { to { transform: rotate(360deg); } }

/* ── Player card ── */
.mini-player {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 6px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  height: 36px;
  box-sizing: border-box;
}
/* Play/Pause button — circle */
.mini-playpause {
  width: 28px;
  height: 28px;
  min-width: 28px;
  border-radius: 50%;
  border: none;
  background: #6366f1;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background 0.15s;
}
.mini-playpause:hover { background: #4f46e5; }
/* Scrub bar */
.mini-progress-wrap {
  flex: 1;
  height: 4px;
  background: var(--border-primary);
  border-radius: 2px;
  cursor: pointer;
  overflow: hidden;
}
.mini-progress-fill {
  height: 100%;
  background: #6366f1;
  border-radius: 2px;
  transition: width 0.2s linear;
  max-width: 100%;
}
/* Time */
.mini-time {
  font-size: 11px;
  color: var(--text-secondary);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  font-weight: 500;
  min-width: 72px;
  text-align: center;
}
/* Stop button — square */
.mini-stop {
  width: 26px;
  height: 26px;
  min-width: 26px;
  border-radius: 6px;
  border: 1px solid var(--border-primary);
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: all 0.15s;
}
.mini-stop:hover { border-color: #fca5a5; color: #dc2626; background: #fff1f2; }

.delete-icon-btn {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #EF4444;
  transition: all 0.2s;
  flex-shrink: 0;
}
.delete-icon-btn:hover { background: #FEF2F2; border-color: #FECACA; }

/* TTS Preview */
.tts-preview {
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
  border-radius: 10px;
  padding: 14px;
  margin-top: 8px;
}
.preview-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  margin: 0;
}

.upload-info {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #10B981;
  margin-top: 8px;
}

/* Buttons */
.btn-primary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #3B82F6;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
}
.btn-primary:hover:not(:disabled) { background: #2563EB; transform: translateY(-1px); }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-secondary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  transition: all 0.2s;
}
.btn-secondary:hover:not(:disabled) { background: var(--bg-tertiary); }
.btn-secondary:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-danger {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #EF4444;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
}
.btn-danger:hover:not(:disabled) { background: #DC2626; }
.btn-danger:disabled { opacity: 0.6; cursor: not-allowed; }

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.modal-container {
  background: var(--bg-primary);
  border-radius: 16px;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
}
.modal-sm { max-width: 400px; }
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 0;
}
.modal-header h2 {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}
.modal-close {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  transition: all 0.2s;
}
.modal-close:hover { background: var(--bg-tertiary); color: var(--text-primary); }
.modal-body { padding: 20px 24px; }
.modal-footer {
  padding: 0 24px 24px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}
.form-group { margin-bottom: 16px; }
.form-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 6px;
}
.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--border-primary);
  border-radius: 10px;
  font-size: 15px;
  background: var(--bg-primary);
  color: var(--text-primary);
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.form-input:focus {
  outline: none;
  border-color: #3B82F6;
  box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
}
.form-textarea { resize: vertical; font-family: inherit; }
.form-select { appearance: auto; }
.file-input { padding: 10px 12px; font-size: 13px; }

.confirm-text {
  font-size: 15px;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.5;
}

/* Drop zone */
.drop-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 36px 24px;
  border: 2px dashed var(--border-primary);
  border-radius: 12px;
  background: var(--bg-secondary);
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  text-align: center;
}
.drop-zone:hover,
.drop-zone.drag-over {
  border-color: #3B82F6;
  background: rgba(59,130,246,0.05);
}
.drop-zone.drag-over { border-style: solid; }
.drop-zone.has-file {
  flex-direction: row;
  padding: 20px 24px;
  gap: 14px;
  border-color: rgba(16,185,129,0.4);
  background: rgba(16,185,129,0.04);
}
.drop-zone.has-file:hover {
  border-color: #10B981;
  background: rgba(16,185,129,0.08);
}
.drop-icon { color: #3B82F6; opacity: 0.6; }
.drop-file-icon { color: #10B981; flex-shrink: 0; }
.drop-primary {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
}
.drop-secondary {
  font-size: 13px;
  color: var(--text-secondary);
}
.drop-file-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
  text-align: left;
  min-width: 0;
}
.drop-file-name {
  font-size: 14px;
  font-weight: 600;
  color: #10B981;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.drop-file-size {
  font-size: 12px;
  color: var(--text-secondary);
}
.upload-error {
  font-size: 13px;
  color: #EF4444;
  background: rgba(239,68,68,0.08);
  border: 1px solid rgba(239,68,68,0.2);
  border-radius: 8px;
  padding: 8px 12px;
  margin: 8px 0 0;
}
</style>
